import * as React from 'react';
import { connect } from 'react-redux';

import type ScheduleBoardEmployee from 'ab-viewModels/scheduleBoardEmployee.viewModel';
import type { WorkOrderResourceLookupModel } from 'ab-viewModels/scheduleBoardWorkOrderModal.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { formatDate } from '@acceligentllc/shared/utils/time';

import type { NotificationStatusEnum } from '@acceligentllc/shared/enums/notificationStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import { getColorTextClass } from 'af-utils/color.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';

type NotificationStatus = {
	smsSentAt: Date | undefined;
	smsStatus: NotificationStatusEnum | undefined;
	emailSentAt: Date | undefined;
	emailStatus: NotificationStatusEnum | undefined;
};

interface OwnProps {
	dueDate: string;
	removed: boolean;
	resourceLookup: WorkOrderResourceLookupModel;
	workOrderStatus: WorkOrderStatus;
}

interface StateProps {
	employeeDetails: ScheduleBoardEmployee | undefined;
	canViewForeman: boolean;
}

type Props = OwnProps & StateProps;

class EmployeeItem extends React.PureComponent<Props> {
	static defaultProps: Partial<Props> = {
		employeeDetails: {} as ScheduleBoardEmployee,
	};

	static renderNotificationStatus = (status: NotificationStatus) => {
		return (
			<div>
				{status.emailSentAt &&
					<div>Email sent at {formatDate(status.emailSentAt, TimeFormat.FULL_DATE)} ({status.emailStatus})</div>
				}
				{status.smsSentAt &&
					<div>SMS sent at {formatDate(status.smsSentAt, TimeFormat.FULL_DATE)} ({status.smsStatus})</div>
				}
			</div>
		);
	};

	getEmployeeLabel = () => {
		const {
			canViewForeman,
			employeeDetails: {
				firstName,
				lastName,
				wageRate,
				duplicateDisplayNameExists,
			} = {},
			resourceLookup: {
				name,
				notification,
				isCrewLead,
			},
		} = this.props;

		const label = firstName ? `${firstName[0]}${duplicateDisplayNameExists ? firstName[1] : ''} ${lastName} ${wageRate}` : name;
		const hasNotificationStatus = !!notification?.smsSentAt || !!notification?.emailSentAt;
		const notificationStatus = {
			smsSentAt: notification?.smsSentAt,
			smsStatus: notification?.smsStatus,
			emailSentAt: notification?.emailSentAt,
			emailStatus: notification?.emailStatus,
		};
		return (
			<span className="work-order-info-modal__resource-item--details">
				<div className={styles['employee-label']}>
					{canViewForeman && isCrewLead && (
						<span className={styles['crew-lead-indicator']} />
					)}
					{label}
				</div>
				{hasNotificationStatus &&
					<Tooltip message={EmployeeItem.renderNotificationStatus(notificationStatus)}>
						<span className="icon-help_fill" />
					</Tooltip>
				}
			</span>
		);
	};

	render() {
		const { resourceLookup, removed } = this.props;
		const { color } = resourceLookup;

		return removed ?
			(
				<Tooltip message="Removed" placement="right">
					<div className="work-order-info-modal__resource-item work-order-info-modal__resource-item--employee work-order-info-modal__resource-item--removed">
						<div className={getColorTextClass(color)}>{this.getEmployeeLabel()}</div>
					</div>
				</Tooltip>
			) : (
				<div className="work-order-info-modal__resource-item work-order-info-modal__resource-item--employee">
					<div className={getColorTextClass(color)}>{this.getEmployeeLabel()}</div>
				</div>
			);
	}
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
	const { resourceLookup: { id: resourceLookupId }, dueDate, workOrderStatus } = ownProps;
	const { workOrdersByDateDictionary, employees } = state.scheduleBoard;
	const { companyData, userData } = state.user;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const canViewForeman = isAllowed(
		PagePermissions.COMPANY.FOREMAN.VIEW,
		permissions,
		isCompanyAdmin,
		role
	);

	let employeeId: number | undefined;
	if (workOrderStatus === WorkOrderStatus.CANCELED) {
		employeeId = workOrdersByDateDictionary?.[dueDate]?.canceledWorkOrderResourceLookups?.[resourceLookupId]?.employeeId;
	} else {
		employeeId = workOrdersByDateDictionary?.[dueDate]?.workOrderResourceLookups?.[resourceLookupId]?.employeeId;
	}

	return {
		employeeDetails: employees && employeeId ? employees[employeeId] : undefined,
		canViewForeman,
	};
};

export default connect<StateProps, null, OwnProps>(mapStateToProps)(EmployeeItem);
